import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  noIndex?: boolean;
  /** Schema.org structured data. Pass an object; it will be JSON-stringified into a script tag. */
  jsonLd?: object | object[];
  /** Optional extra meta entries. */
  meta?: Array<{ name?: string; property?: string; content: string }>;
}

const SITE_NAME = 'talixjobs';

/**
 * Public-facing base URL. We can't rely on `window.location.origin` because at
 * build-time during react-snap pre-rendering, that resolves to the temporary
 * snap server (e.g. `http://localhost:45678`). Use the build-time env var so
 * pre-rendered HTML has correct canonical/og:url values.
 */
function publicBaseUrl(): string {
  const fromEnv = process.env.REACT_APP_PUBLIC_BASE_URL;
  if (fromEnv) return fromEnv.replace(/\/$/, '');
  if (typeof window !== 'undefined') return window.location.origin;
  return 'https://talixjobs.com';
}

const DEFAULT_OG_IMAGE = `${publicBaseUrl()}/talix-jobs-logo.jpg`;

export const SEO: React.FC<SEOProps> = ({
  title,
  description,
  canonicalUrl,
  ogImage,
  ogType = 'website',
  noIndex,
  jsonLd,
  meta = [],
}) => {
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  const base = publicBaseUrl();
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';
  const url = canonicalUrl ?? `${base}${pathname}`;
  const image = ogImage ?? DEFAULT_OG_IMAGE;
  const jsonLdEntries: object[] = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {url && <link rel="canonical" href={url} />}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      {url && <meta property="og:url" content={url} />}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={SITE_NAME} />
      {image && <meta property="og:image" content={image} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      {image && <meta name="twitter:image" content={image} />}

      {meta.map((m, i) => {
        const key = m.name ?? m.property ?? `meta-${i}`;
        if (m.name) return <meta key={key} name={m.name} content={m.content} />;
        if (m.property) return <meta key={key} property={m.property} content={m.content} />;
        return null;
      })}

      {jsonLdEntries.map((entry, idx) => (
        <script key={`jsonld-${idx}`} type="application/ld+json">
          {JSON.stringify(entry)}
        </script>
      ))}
    </Helmet>
  );
};
